import { NavLink, useLocation } from 'react-router-dom'
import {
  History,
  BarChart3,
  ClipboardList,
  Table,
  LayoutGrid,
  Brain,
  Shield,
  Settings as SettingsIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { selectedSidebarClasses } from '@/lib/styles'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { useAppContextOptional } from '@/hooks/useAppContext'
import { useCanAccessAdmin } from '@/hooks/useCanAccessAdmin'

interface NavItem {
  icon: React.ComponentType<{ className?: string }>
  label: string
  tooltip: string
  path: string
  /** Draws a rule after this item. The only grouping the nav has now:
   * the two headings that named the groups were dropped in #2194, and the
   * rule carries the split on its own. */
  dividerAfter?: boolean
  iconColor?: string
  /** Additional pathname-prefix matchers under the workspace
   * scope. The sidebar item is treated as active when any of
   * these are a prefix of the current URL (after the
   * /workspaces/:wid segment). Lets nested dynamic routes —
   * e.g. /variants/:vid/dashboard for Reports — keep the
   * parent sidebar entry highlighted without the route param
   * appearing in the static `path`. */
  activeMatch?: string[]
  /** Sub-trees the active-match check should NOT claim — useful
   * when a parent entry's prefix would otherwise overlap a more
   * specific entry that owns its own subtree (e.g. `/admin/`
   * matches `/admin/analytics/...`, but the Analytics sidebar
   * entry owns that subtree, so the Admin entry must exclude it
   * to avoid two simultaneously-active sidebar items —
   * PR #524 Copilot review). Exclusion wins over inclusion. */
  activeMatchExclude?: string[]
  /** When true, `path` is used verbatim instead of being run through
   * `useWorkspacePath()` (`wp()`). For identity-tier destinations
   * that live outside the workspace scope — today only `/admin/*`
   * (#429 Wave 1 Track C); future cross-org surfaces would set this
   * too. Items with this flag also render WITHOUT a workspace
   * context present (e.g. when the user is already on /admin/*),
   * unlike workspace-scoped items which are hidden in that mode. */
  absolutePath?: boolean
}

/**
 * The nav, ordered by how often each entry is used.
 *
 * Runs and Reports are used every day, so they lead. Tests, Test Data and
 * Agent Memory are the regular working entries. Apps comes last, directly
 * above Settings with no divider between them: once an app and its variants
 * exist, Apps is configuration you rarely touch, the same as Settings.
 * An earlier order put Apps first in the working group to mirror first-run
 * setup; that kept a rarely used entry between the ones used every week.
 */
const navItems: NavItem[] = [
  { icon: History, label: 'Runs', tooltip: 'Start a run, watch it, and review past runs', path: '/results', iconColor: 'text-blue-400' },
  {
    icon: BarChart3,
    label: 'Reports',
    tooltip: 'View regression dashboards per variant',
    path: '/reports',
    dividerAfter: true,
    iconColor: 'text-pink-400',
    // Reports also "owns" the variant-scoped dashboard / run-detail
    // routes (#437). When a customer drills from /reports into
    // /variants/:vid/dashboard or /variants/:vid/runs/:runId, the
    // sidebar entry should stay highlighted so they don't think
    // they've left the reporting surface.
    activeMatch: ['/variants/'],
  },
  // Unified Tests entry — replaces the prior Test Plan + Test
  // Suites pair (#542). Old paths (/test-plan, /test-suites)
  // hard-redirect to /tests; the single entry below owns both.
  // activeMatch keeps the entry highlighted when a deep-link
  // hits the old paths (the redirect happens, but for the brief
  // moment before navigation completes the sidebar should still
  // light up the right entry).
  {
    icon: ClipboardList,
    label: 'Tests',
    tooltip: 'Test cases + suites in one view',
    path: '/tests',
    iconColor: 'text-amber-500',
    activeMatch: ['/test-plan', '/test-suites'],
  },
  { icon: Table, label: 'Test Data', tooltip: 'Define test inputs, credentials, and shared data', path: '/test-data', iconColor: 'text-cyan-400' },
  // "Agent Memory" replaces "AI Hints" — same data and route. The
  // pre-existing route `/hints` is preserved so deep-links don't
  // break; only the surface name + icon move with the user's
  // preferred framing of "persistent memory" over "hints corpus."
  { icon: Brain, label: 'Agent Memory', tooltip: 'Patterns the agents have learned across runs', path: '/hints', dividerAfter: true, iconColor: 'text-emerald-400' },
  // Set-once configuration. Settings renders right after this with no
  // divider between them, so the two read as one group.
  { icon: LayoutGrid, label: 'Apps', tooltip: 'Configure applications and variants', path: '/applications', iconColor: 'text-orange-400' },
]

/** Admin entry — identity-tier (#429 Wave 1 Track C). Lives outside
 * the workspace scope (`/admin`, not `/workspaces/:wid/admin`), so
 * `absolutePath: true` bypasses the `wp()` workspace prefix. Gated
 * by `useCanAccessAdmin()` — Wave 2 wired the real superadmin check;
 * the sidebar entry is shown to users with `isSuperadmin: true`.
 * The `x-superadmin-mode: on` opt-in (tracked separately by
 * `useSuperadminMode()` / `apiFetch`) is the per-request escalation
 * gate, NOT a sidebar-visibility gate.
 *
 * Single entry for the entire admin tier: the /admin index page
 * itself is the discovery surface for every admin feature (Runtime
 * Config, Analytics, Pricing, Audit Log) via its iconed-card grid.
 * That keeps the sidebar uncluttered as Wave 3+ adds admin
 * sub-surfaces. */
const adminItem: NavItem = {
  icon: Shield,
  label: 'Global Admin',
  tooltip: 'Tools for running this installation, across every organization',
  path: '/admin',
  iconColor: 'text-rose-400',
  absolutePath: true,
  // Highlight whenever the user is anywhere under `/admin/*` — the
  // index page itself owns discovery for every admin sub-feature,
  // so the single sidebar entry stays active across them all
  // (Config, Analytics, Pricing, future sub-routes). NavLink's own
  // `isActive` handles the exact `/admin` match; this matcher
  // covers every sub-route.
  activeMatch: ['/admin/'],
}

/** True when the given pathname matches one of the `matchers`
 * prefixes. For workspace-scoped items, the `/workspaces/:wid`
 * prefix is stripped first so matchers can stay relative
 * (`/variants/` lights up the Reports entry for any of
 * `/workspaces/<wid>/variants/<id>/dashboard`,
 * `/workspaces/<wid>/variants/<id>/runs/<rid>`, etc). For
 * absolute-path items (e.g. `/admin/*`), no stripping happens —
 * the matchers are full-pathname prefixes. */
function matchesActive(
  pathname: string,
  matchers: string[] | undefined,
  absolutePath: boolean,
  excludes?: string[],
): boolean {
  if (!matchers || matchers.length === 0) return false
  const target = absolutePath
    ? pathname
    : pathname.replace(/^\/workspaces\/[^/]+/, '')
  // Exclusion wins over inclusion — `/admin/analytics/...` should
  // belong to the Analytics entry, not the broader Admin entry.
  if (excludes && excludes.some((e) => target.startsWith(e))) return false
  return matchers.some((m) => target.startsWith(m))
}

export function Sidebar() {
  const wp = useWorkspacePath()
  const location = useLocation()
  // Workspace context is optional — present under /workspaces/:wid/*,
  // absent under /admin/* (#429 Wave 1 Track C). When absent we hide
  // every workspace-scoped nav entry and show only the absolute-path
  // ones (Admin + admin-tier Analytics). The Sidebar component stays
  // single for both modes per the anti-sprawl rule — no AdminSidebar
  // variant.
  const appCtx = useAppContextOptional()
  const canAdmin = useCanAccessAdmin()

  // Two visibility seams composed into the JSX below:
  //   1. Workspace-scoped entries — only render when AppContext is
  //      present. Without it (admin tier) we'd render NavLinks
  //      pointing at `/workspaces/undefined/...`, so suppress
  //      entirely. Analytics used to live here but moved to admin-
  //      tier in #429 Wave 3 Track E.
  //   2. Admin — identity-tier, gated on `useCanAccessAdmin()`
  //      (real superadmin check). A single entry: the /admin index
  //      page is the discovery surface for every admin sub-feature,
  //      so the sidebar stays uncluttered. Renders in both modes
  //      (workspace and admin) so a superadmin always has a click
  //      target back into the admin tier from any page.

  // Per-item renderer. Vertical icon-stacked layout (84px wide
  // entries) — restored after PR 1's brief widening to 220px.
  // The wider horizontal layout looked off once the Context pill
  // landed inside it; the user-feedback reversal moved the picker
  // out of the sidebar entirely (now lives at the top of the
  // Settings page), and the sidebar reverted to its original
  // icon-stacked aesthetic. See the revised ADR 0010 Appendix B
  // for the placement decision rationale.
  function renderNavItem(item: NavItem) {
    const customActive = matchesActive(
      location.pathname,
      item.activeMatch,
      item.absolutePath === true,
      item.activeMatchExclude,
    )
    const to = item.absolutePath ? item.path : wp(item.path)
    return (
      <div key={item.path} className="w-full flex flex-col items-center">
        <NavLink
          to={to}
          // Items with an explicit exclusion list use `end={true}`
          // so NavLink's default loose match doesn't bypass the
          // exclusion. Without this the Admin entry's NavLink
          // would consider itself active on `/admin/analytics`
          // (loose match on the `/admin` prefix) even though
          // activeMatchExclude says that subtree belongs to the
          // Analytics entry — PR #524 Copilot review.
          end={item.activeMatchExclude ? true : undefined}
          className={({ isActive }) =>
            cn(
              'flex flex-col items-center justify-center w-16 h-16 rounded-lg mb-1 transition-colors',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              isActive || customActive
                ? selectedSidebarClasses
                : 'text-sidebar-foreground/70'
            )
          }
          title={item.tooltip}
        >
          {/* Per-item icon colors stay visible in BOTH active and
              inactive states — with the border-style selected look
              there's no fill inversion to fight against, so the
              brand colors can stay on through. */}
          <item.icon className={cn('h-6 w-6 mb-1', item.iconColor)} />
          {/* `text-center` + `leading-tight` keeps two-line labels
              like "Agent Memory" centered and snug inside the
              64px-wide entry. */}
          <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
        </NavLink>
        {item.dividerAfter && (
          <div className="w-10 h-px bg-sidebar-border my-2" />
        )}
      </div>
    )
  }

  return (
    <aside className="fixed left-0 top-14 z-40 h-[calc(100dvh-3.5rem)] w-[84px] border-r bg-sidebar overflow-y-auto">
      <nav className="flex flex-col items-center py-3">
        {/* No workspace switcher here — it lives in the header,
            immediately left of the app/variant dropdown. Earlier
            iteration tried a sidebar tile above the nav stack but
            it gave too much prominence to "switch" relative to
            navigation, which was off; the header-pill placement
            keeps switching one click away without dominating the
            icon-stacked sidebar aesthetic. See Header.tsx for the
            mount and ADR 0010 Appendix B for the placement
            journey. */}
        {appCtx && navItems.map(renderNavItem)}

        {/* Settings entry — single sidebar link to the unified
            /settings page. The Org/Workspace context picker that
            briefly lived alongside this entry (PR 1 of #547) moved
            INTO the Settings page itself; the sidebar stays
            uncluttered and visually consistent. Workspace-scoped:
            hidden in admin-only mode since the Settings page
            requires a workspace context. No divider before it: it
            shares a group with Apps, the last entry above. */}
        {appCtx?.workspaceId && (
          <>
            {renderNavItem({
              icon: SettingsIcon,
              label: 'Settings',
              tooltip: 'Account, workspace, and organization settings',
              path: '/settings',
              iconColor: 'text-slate-400',
            })}
          </>
        )}

        {canAdmin && (
          <>
            <div className="w-10 h-px bg-sidebar-border my-2" />
            {renderNavItem(adminItem)}
          </>
        )}
      </nav>
    </aside>
  )
}
